import { I18n } from '@app/i18n/strings-keys';
import { ENTITY_TYPES } from '@shared/types';
import { get, startCase } from 'lodash';

type IEntityConfig = {
  [key in Exclude<ENTITY_TYPES, ENTITY_TYPES.HAWKER_CONFIG>]: IEntityNameConfig;
};

interface IEntityNameConfig {
  singular: string;
  plural: string;
  customListTitle?: string;
}

export const ENTITY_NAME_CONFIG: IEntityConfig = {
  [ENTITY_TYPES.DASHBOARD]: {
    singular: I18n.EntityNames.Dashboard,
    plural: I18n.EntityNames.Dashboards,
  },
  [ENTITY_TYPES.ORDER]: {
    singular: I18n.EntityNames.InventoryOrder,
    plural: I18n.EntityNames.InventoryOrders,
    customListTitle: I18n.Routes.InventoryOrderListTitle,
  },
  [ENTITY_TYPES.VENDOR]: {
    singular: I18n.EntityNames.VENDOR,
    plural: I18n.EntityNames.VENDOR,
  },
  [ENTITY_TYPES.INVENTORY_ITEM_VENUE]: {
    singular: I18n.EntityNames.InventoryItem,
    plural: I18n.EntityNames.InventoryItems,
  },
  [ENTITY_TYPES.INVENTORY_ITEM]: {
    singular: I18n.EntityNames.InventoryItem,
    plural: I18n.EntityNames.InventoryItems,
  },
  [ENTITY_TYPES.SITE]: {
    singular: I18n.EntityNames.Site,
    plural: I18n.EntityNames.Sites,
  },
  [ENTITY_TYPES.TRANSFER]: {
    singular: I18n.EntityNames.Transfer,
    plural: I18n.EntityNames.Transfers,
    customListTitle: I18n.Routes.TransferListTitle,
  },
  [ENTITY_TYPES.TRANSFER_DESTINATIONS]: {
    singular: I18n.EntityNames.TransferDestination,
    plural: I18n.EntityNames.TransferDestinations,
  },
  [ENTITY_TYPES.EVENTS]: {
    singular: I18n.EntityNames.Event,
    plural: I18n.EntityNames.Events,
  },
  [ENTITY_TYPES.STAND_WORKSHEET]: {
    singular: I18n.EntityNames.StandWorksheet,
    plural: I18n.EntityNames.StandWorksheets,
    customListTitle: I18n.Routes.StandWorksheetListTitle,
  },
  [ENTITY_TYPES.STAND_WORKSHEET_ITEM]: {
    singular: I18n.EntityNames.StandWorksheetItem,
    plural: I18n.EntityNames.StandWorksheetItems,
  },
  [ENTITY_TYPES.RECIPE_VENUE]: {
    singular: I18n.EntityNames.RecipeVenue,
    plural: I18n.EntityNames.RecipeVenues,
  },
  [ENTITY_TYPES.PRODUCT]: {
    singular: I18n.EntityNames.Product,
    plural: I18n.EntityNames.Products,
  },
  [ENTITY_TYPES.PRODUCT_PRICE]: {
    singular: I18n.EntityNames.ProductPrice,
    plural: I18n.EntityNames.ProductPrices,
  },
  [ENTITY_TYPES.INVENTORY_COUNT]: {
    singular: I18n.EntityNames.InventoryCount,
    plural: I18n.EntityNames.InventoryCounts,
    customListTitle: I18n.Routes.InventoryCountListTitle,
  },
  [ENTITY_TYPES.INVENTORY_COUNT_GROUP]: {
    singular: I18n.EntityNames.InventoryCount,
    plural: I18n.EntityNames.InventoryCounts,
    customListTitle: I18n.Routes.InventoryCountListTitle,
  },
  [ENTITY_TYPES.TAG]: {
    singular: I18n.EntityNames.Tag,
    plural: I18n.EntityNames.Tags,
    customListTitle: I18n.EntityNames.Tags,
  },
  [ENTITY_TYPES.EVENT_TYPE]: {
    singular: I18n.EntityNames.EventTypes,
    plural: I18n.EntityNames.EventTypes,
  },
  [ENTITY_TYPES.CART]: {
    singular: I18n.EntityNames.Cart,
    plural: I18n.EntityNames.Carts,
  },
  [ENTITY_TYPES.INCOMPLETE_TRANSFER]: {
    singular: I18n.EntityNames.IncompleteTransfer,
    plural: I18n.EntityNames.IncompleteTransfers,
    customListTitle: I18n.Routes.IncompleteTransferListTitle,
  },
  [ENTITY_TYPES.HISTORICAL_QUANTITIES]: {
    singular: I18n.EntityNames.HistoricalTransferQuantity,
    plural: I18n.EntityNames.HistoricalTransferQuantities,
    customListTitle: I18n.Routes.HistoricalTransferQuantityListTitle,
  },
  [ENTITY_TYPES.CURRENCY_SCHEME]: {
    singular: I18n.EntityNames.CurrencyScheme,
    plural: I18n.EntityNames.CurrencySchemes,
  },
  [ENTITY_TYPES.TRANSFER_GROUP]: {
    singular: I18n.EntityNames.TransferGroup,
    plural: I18n.EntityNames.TransferGroups,
    customListTitle: I18n.Routes.TransferGroupListTitle,
  },
  [ENTITY_TYPES.SALE_DEPLETION_ORDER]: {
    singular: I18n.EntityNames.SaleDepletion,
    plural: I18n.EntityNames.SaleDepletions,
    customListTitle: I18n.Routes.SaleDepletionListTitle,
  },
  [ENTITY_TYPES.AUDIT_TRAIL]: {
    singular: I18n.EntityNames.AuditTrail,
    plural: I18n.EntityNames.AuditTrails,
  },
  [ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE]: {
    singular: I18n.EntityNames.TransferGroupTemplate,
    plural: I18n.EntityNames.TransferGroupTemplates,
    customListTitle: I18n.Routes.TransferGroupTemplateListTitle,
  },
  [ENTITY_TYPES.FILTERED_ORDER]: {
    singular: I18n.EntityNames.FilteredOrder,
    plural: I18n.EntityNames.FilteredOrders,
    customListTitle: I18n.Routes.FilteredOrderListTitle,
  },
  [ENTITY_TYPES.FILTERED_TRANSFER]: {
    singular: I18n.EntityNames.FilteredTransfer,
    plural: I18n.EntityNames.FilteredTransfers,
    customListTitle: I18n.Routes.FilteredTransferListTitle,
  },
  [ENTITY_TYPES.TRANSFER_REQUEST]: {
    singular: I18n.EntityNames.TransferRequest,
    plural: I18n.EntityNames.TransferRequests,
    customListTitle: I18n.Routes.TransferRequestListTitle,
  },
  [ENTITY_TYPES.COMMISSION_RATE]: {
    singular: I18n.EntityNames.CommissionRate,
    plural: I18n.EntityNames.CommissionRates,
  },
  [ENTITY_TYPES.HAWKER_PROFILE]: {
    singular: I18n.EntityNames.HawkerProfile,
    plural: I18n.EntityNames.HawkerProfiles,
    customListTitle: I18n.Routes.HawkerProfileListTitle,
  },
  [ENTITY_TYPES.REPORTING_CATEGORY]: {
    singular: I18n.EntityNames.ReportingCategory,
    plural: I18n.EntityNames.ReportingCategories,
  },
  [ENTITY_TYPES.HAWKER_LOAD]: {
    singular: I18n.EntityNames.HawkerLoad,
    plural: I18n.EntityNames.HawkerLoads,
    customListTitle: I18n.EntityNames.HawkerLoad,
  },
  [ENTITY_TYPES.HAWKER_COMMISSARY_ASSIGNMENT]: {
    singular: I18n.EntityNames.HawkerCommissaryAssignment,
    plural: I18n.EntityNames.HawkerCommissaryAssignments,
    customListTitle: I18n.EntityNames.HawkerCommissaryAssignment,
  },
  [ENTITY_TYPES.HAWKER_SESSION]: {
    singular: I18n.EntityNames.HawkerSession,
    plural: I18n.EntityNames.HawkerSessions,
    customListTitle: I18n.EntityNames.HawkerSession,
  },
  [ENTITY_TYPES.HAWKER_COMMISSARY_ASSIGNMENT_HISTORY]: {
    singular: I18n.EntityNames.HawkerCommissaryAssignmentHistory,
    plural: I18n.EntityNames.HawkerCommissaryAssignmentHistories,
    customListTitle: I18n.EntityNames.HawkerCommissaryAssignmentHistory,
  },
  [ENTITY_TYPES.HAWKER_SESSION_ADMIN]: {
    singular: I18n.EntityNames.HawkerSession,
    plural: I18n.EntityNames.HawkerSessions,
    customListTitle: I18n.EntityNames.HawkerSession,
  },
};

export const getSingularEntityName = (entityType: ENTITY_TYPES): string => {
  return get(
    ENTITY_NAME_CONFIG,
    [entityType, 'singular'],
    startCase(entityType),
  );
};

export const getPluralEntityName = (entityType: ENTITY_TYPES): string => {
  return get(ENTITY_NAME_CONFIG, [entityType, 'plural'], startCase(entityType));
};

export const getCustomEntityName = (entityType: ENTITY_TYPES): string => {
  return get(
    ENTITY_NAME_CONFIG,
    [entityType, 'customListTitle'],
    startCase(entityType),
  );
};
